/**
 * DescriptionTab — a document-style project description (TipTap).
 * First line is an H1 title; the rest is rich body: lists (bullet/ordered),
 * checkboxes, text align, images, tables (add/delete row & column), and file
 * embeds (PDF/Excel shown as a link) that also register in the Files tab.
 * Autosaves (debounced + on unmount).
 */
import React, { useEffect, useRef, useState, useCallback } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import TaskList from "@tiptap/extension-task-list";
import TaskItem from "@tiptap/extension-task-item";
import Image from "@tiptap/extension-image";
import TextAlign from "@tiptap/extension-text-align";
import Link from "@tiptap/extension-link";
import { Table, TableRow, TableHeader, TableCell } from "@tiptap/extension-table";
import {
  Bold, Italic, List, ListOrdered, CheckSquare, AlignLeft, AlignRight,
  Image as ImageIcon, Table as TableIcon, Paperclip, Rows, Columns,
  Trash2, Save,
} from "lucide-react";
import { toast } from "../../../utils/toast";
import { wsApi } from "../wsApi";
import { UPLOAD_URL } from "../../../api/api";
import { T } from "../../../components/em/ui";

const EMPTY_DOC = {
  type: "doc",
  content: [
    { type: "heading", attrs: { level: 1 }, content: [] },
    { type: "paragraph" },
  ],
};

const parseDoc = (raw) => {
  if (!raw) return EMPTY_DOC;
  try {
    const d = typeof raw === "string" ? JSON.parse(raw) : raw;
    return d && d.type ? d : EMPTY_DOC;
  } catch {
    return EMPTY_DOC;
  }
};

const IMG_RE = /\.(png|jpe?g|gif|webp|svg)$/i;

export default function DescriptionTab({ projectId, initialDoc, onFilesChanged }) {
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const saveTimer = useRef(null);
  const fileRef = useRef(null);
  const imgRef = useRef(null);

  const editor = useEditor({
    extensions: [
      StarterKit,
      TaskList,
      TaskItem.configure({ nested: true }),
      Image,
      Link.configure({ openOnClick: true, autolink: false }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
    ],
    content: parseDoc(initialDoc),
    onUpdate: () => {
      setDirty(true);
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => doSave(), 1500);
    },
  });

  const doSave = useCallback(async () => {
    if (!editor) return;
    setSaving(true);
    try {
      await wsApi.saveProjectDescription(projectId, editor.getJSON());
      setDirty(false);
    } catch {
      toast.error("Could not save description");
    } finally {
      setSaving(false);
    }
  }, [editor, projectId]);

  // Save on unmount if there are pending changes.
  useEffect(() => () => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
  }, []);

  if (!editor) return null;

  const uploadAndEmbed = async (file, asImage) => {
    if (!file) return;
    try {
      const res = await wsApi.uploadProjectFile(projectId, file, "description");
      const url = res.url;
      if (asImage && IMG_RE.test(file.name)) {
        editor.chain().focus().setImage({ src: absolute(url) }).run();
      } else {
        // PDF/Excel/etc → insert a link (shown as a link, per spec)
        editor
          .chain()
          .focus()
          .insertContent(
            `<p><a href="${absolute(url)}" target="_blank" rel="noreferrer">📎 ${escapeHtml(file.name)}</a></p>`,
          )
          .run();
      }
      onFilesChanged && onFilesChanged();
    } catch {
      toast.error("Could not upload file");
    }
  };

  const inTable = editor.isActive("table");

  return (
    <div>
      {/* Toolbar */}
      <div className="ws-doc-toolbar" style={toolbar}>
        <Tb on={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()} Icon={Bold} title="Bold" />
        <Tb on={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()} Icon={Italic} title="Italic" />
        <Sep />
        <Tb on={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()} Icon={List} title="Bullet list" />
        <Tb on={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()} Icon={ListOrdered} title="Numbered list" />
        <Tb on={editor.isActive("taskList")} onClick={() => editor.chain().focus().toggleTaskList().run()} Icon={CheckSquare} title="Checklist" />
        <Sep />
        <Tb on={editor.isActive({ textAlign: "left" })} onClick={() => editor.chain().focus().setTextAlign("left").run()} Icon={AlignLeft} title="Align left" />
        <Tb on={editor.isActive({ textAlign: "right" })} onClick={() => editor.chain().focus().setTextAlign("right").run()} Icon={AlignRight} title="Align right" />
        <Sep />
        <Tb onClick={() => imgRef.current?.click()} Icon={ImageIcon} title="Insert image" />
        <Tb onClick={() => fileRef.current?.click()} Icon={Paperclip} title="Attach file (PDF/Excel)" />
        <Tb onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()} Icon={TableIcon} title="Insert table" />
        {inTable && (
          <>
            <Sep />
            <Tb onClick={() => editor.chain().focus().addRowAfter().run()} Icon={Rows} title="Add row" />
            <Tb onClick={() => editor.chain().focus().deleteRow().run()} Icon={Rows} title="Delete row" danger />
            <Tb onClick={() => editor.chain().focus().addColumnAfter().run()} Icon={Columns} title="Add column" />
            <Tb onClick={() => editor.chain().focus().deleteColumn().run()} Icon={Columns} title="Delete column" danger />
            <Tb onClick={() => editor.chain().focus().deleteTable().run()} Icon={Trash2} title="Delete table" danger />
          </>
        )}
        <div style={{ flex: 1 }} />
        <span style={{ font: `500 11px ${T.font}`, color: dirty ? T.orange : T.fg7, display: "inline-flex", alignItems: "center", gap: 5 }}>
          <Save size={12} /> {saving ? "Saving…" : dirty ? "Unsaved" : "Saved"}
        </span>
      </div>

      <style>{DOC_CSS}</style>
      <div style={docWrap}>
        <EditorContent editor={editor} className="ws-doc" />
      </div>

      <input ref={imgRef} type="file" accept="image/*" style={{ display: "none" }}
        onChange={(e) => { uploadAndEmbed(e.target.files?.[0], true); e.target.value = ""; }} />
      <input ref={fileRef} type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.ppt,.pptx" style={{ display: "none" }}
        onChange={(e) => { uploadAndEmbed(e.target.files?.[0], false); e.target.value = ""; }} />
    </div>
  );
}

function Tb({ on, onClick, Icon, title, danger }) {
  return (
    <button type="button" title={title} onClick={onClick}
      style={{
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        width: 30, height: 30, borderRadius: 7, border: 0, cursor: "pointer",
        background: on ? T.orange50 : "transparent",
        color: danger ? T.danger : on ? T.orange : T.fg4,
      }}>
      <Icon size={15} />
    </button>
  );
}
const Sep = () => <span style={{ width: 1, height: 18, background: T.border, margin: "0 3px" }} />;

// Embedded images/files are /uploads/* paths — resolve them against UPLOAD_URL
// (the /backend-prefixed static host), not the API root.
const absolute = (url) => (url && url.startsWith("http") ? url : `${UPLOAD_URL}${url}`);
const escapeHtml = (s) => String(s).replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));

const toolbar = {
  display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap",
  padding: "8px 10px", border: `1px solid ${T.border}`, borderBottom: 0,
  borderRadius: "12px 12px 0 0", background: "var(--em-bg-card)",
  position: "sticky", top: 0, zIndex: 5,
};
const docWrap = {
  border: `1px solid ${T.border}`, borderRadius: "0 0 12px 12px",
  background: "var(--em-bg-card)", padding: "24px 28px", minHeight: 360,
};
const DOC_CSS = `
/* Global index.css styles buttons/svgs app-wide (button { transition: all } +
   button svg { width/height 16px; position: relative }) which collapses the
   toolbar icons to 0 width here — pin them down with a scoped override. */
.ws-doc-toolbar button { transition: none; padding: 0; margin: 0; line-height: 1; }
.ws-doc-toolbar button svg { width: 15px !important; height: 15px !important; position: static !important; flex-shrink: 0; }
.ws-doc .ProseMirror { outline: none; min-height: 320px; font: 400 14px/1.7 var(--em-font, inherit); color: var(--em-fg2); }
.ws-doc .ProseMirror:focus { outline: none; }
.ws-doc .ProseMirror h1 { font-size: 26px; font-weight: 800; letter-spacing: -0.02em; margin: 0 0 12px; color: var(--em-fg1); }
.ws-doc .ProseMirror h2 { font-size: 19px; font-weight: 700; margin: 18px 0 8px; color: var(--em-fg1); }
.ws-doc .ProseMirror p { margin: 6px 0; }
/* The global reset in index.css does \`ol, ul { list-style: none }\` app-wide,
   which hides the bullets/numbers here — restore the markers for doc lists
   only (the taskList below keeps list-style: none, it draws checkboxes). */
.ws-doc .ProseMirror ul, .ws-doc .ProseMirror ol { padding-left: 24px; margin: 6px 0; }
.ws-doc .ProseMirror ul { list-style: disc outside; }
.ws-doc .ProseMirror ol { list-style: decimal outside; }
.ws-doc .ProseMirror ul ul { list-style-type: circle; }
.ws-doc .ProseMirror ul ul ul { list-style-type: square; }
.ws-doc .ProseMirror ol ol { list-style-type: lower-alpha; }
.ws-doc .ProseMirror ol ol ol { list-style-type: lower-roman; }
.ws-doc .ProseMirror li { display: list-item; margin: 2px 0; }
.ws-doc .ProseMirror li > p { margin: 0; }
.ws-doc .ProseMirror ul[data-type="taskList"] { list-style: none; padding-left: 4px; }
.ws-doc .ProseMirror ul[data-type="taskList"] li { display: flex; gap: 8px; align-items: flex-start; }
.ws-doc .ProseMirror ul[data-type="taskList"] li > label { margin-top: 2px; }
.ws-doc .ProseMirror img { max-width: 100%; border-radius: 8px; margin: 8px 0; }
.ws-doc .ProseMirror a { color: #f54b01; text-decoration: underline; }
.ws-doc .ProseMirror table { border-collapse: collapse; width: 100%; margin: 12px 0; overflow: hidden; }
.ws-doc .ProseMirror td, .ws-doc .ProseMirror th { border: 1px solid var(--em-border); padding: 7px 10px; min-width: 60px; vertical-align: top; }
.ws-doc .ProseMirror th { background: var(--em-bg-head); font-weight: 700; }
.ws-doc .ProseMirror .selectedCell:after { background: rgba(245,75,1,0.08); content: ""; position: absolute; inset: 0; pointer-events: none; }
.ws-doc .ProseMirror h1:empty::before { content: "Untitled project"; color: var(--em-fg7); pointer-events: none; float: left; height: 0; }
`;
